import React, { useState, useEffect } from 'react';
import { payoutAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  DollarSign, 
  Building2, 
  CreditCard, 
  Plus, 
  Loader2, 
  CheckCircle, 
  Clock, 
  XCircle,
  Trash2,
  ArrowDownRight,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';

const Payouts = () => {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [showAddBank, setShowAddBank] = useState(false);
  const [showRequestPayout, setShowRequestPayout] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // New bank account form
  const [bankForm, setBankForm] = useState({
    account_holder_name: '',
    account_number_last4: '',
    routing_number_last4: '',
    bank_name: '',
    account_type: 'checking',
    is_default: true
  });

  // Payout request
  const [payoutAmount, setPayoutAmount] = useState('');
  const [selectedBankId, setSelectedBankId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [balanceRes, accountsRes, historyRes] = await Promise.all([
        payoutAPI.getBalance(),
        payoutAPI.getBankAccounts(),
        payoutAPI.getPayoutHistory()
      ]);
      setBalance(balanceRes.data);
      setBankAccounts(accountsRes.data);
      setPayoutHistory(historyRes.data);
      
      // Set default bank account for payout
      const defaultAccount = accountsRes.data.find(a => a.is_default);
      if (defaultAccount) {
        setSelectedBankId(defaultAccount.id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load payout data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBankAccount = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await payoutAPI.addBankAccount(bankForm);
      toast.success('Bank account added');
      setShowAddBank(false);
      setBankForm({
        account_holder_name: '',
        account_number_last4: '',
        routing_number_last4: '',
        bank_name: '',
        account_type: 'checking',
        is_default: true
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add bank account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBankAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    
    try {
      await payoutAPI.deleteBankAccount(accountId);
      toast.success('Bank account deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bank account');
    }
  };

  const handleRequestPayout = async (e) => {
    e.preventDefault();
    const amount = parseFloat(payoutAmount);
    
    if (isNaN(amount) || amount < 10) {
      toast.error('Minimum payout amount is $10');
      return;
    }
    
    if (amount > (balance?.available_balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setSubmitting(true);
    try {
      const response = await payoutAPI.requestPayout(amount, selectedBankId || null);
      toast.success(response.data.message);
      setShowRequestPayout(false);
      setPayoutAmount('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'processing':
      case 'pending':
        return <Clock className="w-5 h-5 text-amber-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'processing':
      case 'pending':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'failed':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="payouts-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Payouts
        </h1>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-green-500 to-green-600 border-0 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Wallet className="w-6 h-6 opacity-80" />
              </div>
              <p className="text-sm opacity-80">Available Balance</p>
              <p className="text-2xl font-display font-bold">
                ${balance?.available_balance?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Total Earnings</p>
              <p className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                ${balance?.total_earnings?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Pending Payouts</p>
              <p className="text-2xl font-display font-bold text-amber-600">
                ${balance?.pending_payouts?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Total Withdrawn</p>
              <p className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                ${balance?.total_withdrawn?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Request Payout Button */}
        <div className="flex gap-4 mb-8">
          <Button
            onClick={() => setShowRequestPayout(true)}
            disabled={!balance?.available_balance || balance.available_balance < 10}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="request-payout-btn"
          >
            <ArrowDownRight className="w-4 h-4 mr-2" />
            Request Payout
          </Button>
          <Button
            onClick={() => setShowAddBank(true)}
            variant="outline"
            data-testid="add-bank-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Bank Account
          </Button>
        </div>

        {/* Request Payout Modal */}
        {showRequestPayout && (
          <Card className="mb-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight">Request Payout</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRequestPayout} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                    Amount (min $10, max ${balance?.available_balance?.toFixed(2)})
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="number"
                      value={payoutAmount}
                      onChange={(e) => setPayoutAmount(e.target.value)}
                      placeholder="0.00"
                      min="10"
                      max={balance?.available_balance || 0}
                      step="0.01"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {bankAccounts.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                      Bank Account
                    </label>
                    <select
                      value={selectedBankId}
                      onChange={(e) => setSelectedBankId(e.target.value)}
                      className="w-full p-2 border rounded-lg bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                    >
                      {bankAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.bank_name} - ****{account.account_number_last4}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Request Payout'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowRequestPayout(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Add Bank Account Modal */}
        {showAddBank && (
          <Card className="mb-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight">Add Bank Account</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddBankAccount} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                      Account Holder Name
                    </label>
                    <Input
                      value={bankForm.account_holder_name}
                      onChange={(e) => setBankForm({ ...bankForm, account_holder_name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                      Bank Name
                    </label>
                    <Input
                      value={bankForm.bank_name}
                      onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                      placeholder="Chase Bank"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                      Account Number (last 4 digits)
                    </label>
                    <Input
                      value={bankForm.account_number_last4}
                      onChange={(e) => setBankForm({ ...bankForm, account_number_last4: e.target.value.slice(-4) })}
                      placeholder="1234"
                      maxLength={4}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                      Routing Number (last 4 digits)
                    </label>
                    <Input
                      value={bankForm.routing_number_last4}
                      onChange={(e) => setBankForm({ ...bankForm, routing_number_last4: e.target.value.slice(-4) })}
                      placeholder="5678"
                      maxLength={4}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                    Account Type
                  </label>
                  <select
                    value={bankForm.account_type}
                    onChange={(e) => setBankForm({ ...bankForm, account_type: e.target.value })}
                    className="w-full p-2 border rounded-lg bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                  >
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" disabled={submitting} className="bg-red-600 hover:bg-red-700">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Account'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddBank(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Bank Accounts */}
        <Card className="mb-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              Bank Accounts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {bankAccounts.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 text-center py-4">
                No bank accounts added yet
              </p>
            ) : (
              <div className="space-y-3">
                {bankAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white">
                          {account.bank_name}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {account.account_type} • ****{account.account_number_last4}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {account.is_default && (
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                          Default
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteBankAccount(account.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payout History */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight">Payout History</CardTitle>
          </CardHeader>
          <CardContent>
            {payoutHistory.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 text-center py-4">
                No payouts yet
              </p>
            ) : (
              <div className="space-y-3">
                {payoutHistory.map((payout) => (
                  <div
                    key={payout.id}
                    className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(payout.status)}
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white">
                          ${payout.amount.toFixed(2)}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {payout.bank_name} • ****{payout.account_last4}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(payout.status)}`}>
                        {payout.status}
                      </span>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(payout.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Payouts;
